import type { AuthFlowContext, MfaSetupSecret, PasswordChange } from '@aka/ui'
import type { Router } from 'vue-router'

import { ApiError, isNetworkFailure, machineCode } from '@/api'
import { forgotPassword, resetPassword } from '@/api/auth'
import type { useAuthStore } from '@/stores/auth'

export interface AuthFlowDeps {
  auth: ReturnType<typeof useAuthStore>
  router: Router
  t: AuthFlowContext['t']
}

/** The library's pre-console pages speak snake_case to the API through here. */
function change(body: PasswordChange) {
  return {
    email: body.email,
    token: body.token,
    password: body.password,
    password_confirmation: body.passwordConfirmation,
  }
}

/**
 * What the library's pre-console pages (MFA setup, verify, recovery, forgot,
 * reset, invite) need from this console: the auth store for the session, the
 * router for the links between them and the catalogue for their strings.
 * Everything here is a mapping; the behaviour lives in the store and the API.
 */
export function createAuthFlow({ auth, router, t }: AuthFlowDeps): AuthFlowContext {
  return {
    t,
    mfaExpiredMessage: () => t('mfa.expired'),
    // Anything but an ApiError is a bug in the console: null tells the form to
    // rethrow it for the error reporter.
    describeFailure: (error) =>
      error instanceof ApiError
        ? {
            status: error.status,
            message: isNetworkFailure(error) ? t('error.network') : error.message,
            code: machineCode(error),
            requestId: error.requestId,
            fields: error.errors,
          }
        : null,
    href: (target) => router.resolve({ name: target }).href,
    navigate: async (target) => {
      await router.push({ name: target })
    },
    beginMfaSetup: async (): Promise<MfaSetupSecret> => {
      const setup = await auth.beginMfaSetup()
      return { secret: setup.secret, otpauthUrl: setup.otpauth_url }
    },
    completeMfaSetup: (code) => auth.completeMfaSetup(code),
    confirmMfa: (code) => auth.confirmMfa(code),
    recover: (code) => auth.recover(code),
    acceptInvite: (body) => auth.acceptInvite(change(body)),
    abandonSignIn: () => auth.abandonSignIn(),
    forgotPassword,
    resetPassword: (body) => resetPassword(change(body)),
  }
}
